import { useSearchParams } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { GeneralTab } from './GeneralTab';
import { InvoiceTab } from './InvoiceTab';
import { QuoteTab } from './QuoteTab';
import { AgreementTab } from './AgreementTab';
import { PayrollTab } from './PayrollTab';

const TABS = ['general', 'invoice', 'quote', 'agreement', 'payroll'] as const;
type TabKey = (typeof TABS)[number];

/**
 * Document Settings — one page, five tabs.
 *   General  : company identity / address / logo / theme / signatures / company prefix / tax id
 *   Invoice  : invoice numbering, footer, payment defaults, invoice preview
 *   Quote    : quotation numbering, validity, footer, quotation preview
 *   Agreement: agreement numbering, legal defaults, templates manager, agreement preview
 *   Payroll  : payslip identifier labels, employee-code numbering
 *
 * The active tab is reflected in the URL (?tab=…) so each is deep-linkable.
 */
export function DocumentSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading, settings } = useOrganizationSettings();

  const raw = searchParams.get('tab');
  const active: TabKey = TABS.includes(raw as TabKey) ? (raw as TabKey) : 'general';

  const onTabChange = (next: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    });
  };

  if (isLoading || !settings) {
    return <PageContainer title="Document Settings"><div className="text-muted-foreground text-sm">Loading…</div></PageContainer>;
  }

  return (
    <PageContainer
      title="Document Settings"
      description="Branding, numbering, and defaults for the documents you send clients. General settings apply everywhere; each document type has its own tab."
    >
      <Tabs value={active} onValueChange={onTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="invoice">Invoice</TabsTrigger>
          <TabsTrigger value="quote">Quote</TabsTrigger>
          <TabsTrigger value="agreement">Agreement</TabsTrigger>
          <TabsTrigger value="payroll">Payroll</TabsTrigger>
        </TabsList>

        <TabsContent value="general"><GeneralTab /></TabsContent>
        <TabsContent value="invoice"><InvoiceTab /></TabsContent>
        <TabsContent value="quote"><QuoteTab /></TabsContent>
        <TabsContent value="agreement"><AgreementTab /></TabsContent>
        <TabsContent value="payroll"><PayrollTab /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}
